// LandPrepPopup.jsx

import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  IconButton,
  Grid,
  InputLabel,
  TextField,
  Select,
  MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { makeStyles } from "@mui/styles";
import DatePicker from "react-datepicker";
import CustomButton from "../../Custom/CustomButton";
import instance from "../../../globals/axios";
import { getFarmerPlotId } from "../../../globals/utils";
import { useAlert } from "../../Alert";
import EditIcon from "../../../assets/svg/EditIcon.svg";
import SuccessPopup from "./SuccessPopup";

const useStyles = makeStyles((theme) => ({
  closeButton: {
    position: "absolute",
    top: theme.spacing(1),
    right: theme.spacing(1),
    fontSize: 16,
  },
  dialogContent: {
    height: 400,
  },
  datepicker: {
    display: "none",
  },
  taxtLabel: {
    fontSize: "12px !important",
    fontFamily: "Inter",
    fontWeight: "400 !important",
    color: "#000",
    marginTop: "10px",
  },
  taxtTitle: {
    fontSize: "12px !important",
    fontFamily: "Inter",
    fontWeight: "700 !important",
    color: "#000",
  },
}));

const PlantingPopup = ({ onClose, t }) => {
  const classes = useStyles();
  const [open, setOpen] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [formData, setFormData] = useState({});
  const [comment, setComment] = useState("");
  const { showAlert } = useAlert();
  const [existingData, setExistingData] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [errors, setErrors] = useState({});
  const [dateError, setDateError] = useState(false);
  const [rows, setRows] = useState([]);
  let farmerPlotId = getFarmerPlotId();
  const scrollableContainerStyle = {
    maxHeight: "300px",
    overflowY: "auto",
  };

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const response = await instance.get("/plotengagement/get-inches");
        const optionsData = response.data;
        const options = {
          BED_SIZE: [],
          ROW_TO_ROW: [],
          PLANT_TO_PLANT: [],
          DEPTH: [],
          "Soil type": ["Heavy", "Medium", "Light"],
        };
        optionsData.forEach((option) => {
          options[option.stage].push(option.inch);
        });
        const updatedRows = [
          {
            label: t("Bed Size (inch)"),
            type: "dropdown",
            options: options["BED_SIZE"],
            key: "Bed Size (inch)",
          },
          {
            label: t("Row to row spacing (inch)"),
            type: "dropdown",
            options: options["ROW_TO_ROW"],
            key: "Row to row spacing (inch)",
          },
          {
            label: t("Plant to plant spacing (inch)"),
            type: "dropdown",
            options: options["PLANT_TO_PLANT"],
            key: "Plant to plant spacing (inch)",
          },
          {
            label: t("Depth (inch)"),
            type: "dropdown",
            options: options["DEPTH"],
            key: "Depth (inch)",
          },
          {
            label: t("Soil type"),
            type: "dropdown",
            options: options["Soil type"],
            key: "Soil type",
          },
        ];

        setRows(updatedRows);
      } catch (error) {
        console.error("Error fetching options:", error);
      }
    };

    fetchOptions();
  }, []);
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await instance(
          `plotengagement/get-planting?farmer_plot=${farmerPlotId}`
        );
        let data = response.data;
        if (data.length > 0) {
          setExistingData(data[0]);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    if (farmerPlotId) {
      fetchData();
    }
  }, [farmerPlotId]);
  useEffect(() => {
    if (existingData) {
      setSelectedDate(new Date(existingData.planting_date));
      setComment(existingData.comments || "");
      setFormData({
        "bed size (inch)": existingData.bed_size || "",
        "row to row spacing (inch)": existingData.row_to_row_spacing || "",
        "plant to plant spacing (inch)":
          existingData.plant_to_plant_spacing || "",
        "depth (inch)": existingData.depth || "",
        "soil type": existingData.soil_type || "",
      });
    }
  }, [existingData]);

  const handleEditClick = () => {
    setIsEditMode(true);
  };

  const handleCommentChange = (event) => {
    setComment(event.target.value);
  };
  const handleDateChange = (date) => {
    setSelectedDate(date);
    if (dateError && date !== null) {
      setDateError(false);
      setErrors({ ...errors, date: "" });
    }
  };
  const handleChange = (field, value) => {
    if (errors[field] && value !== "") {
      setErrors({ ...errors, [field]: "" });
    }
    setFormData((prevData) => ({
      ...prevData,
      [field]: value,
    }));
  };

  const validateForm = () => {
    let isValid = true;
    const newErrors = {};
    if (selectedDate === null) {
      newErrors.date = "Please select a date.";
      setDateError(true);
      isValid = false;
    } else {
      setDateError(false);
    }
    const dropdownFields = [
      "bed size (inch)",
      "row to row spacing (inch)",
      "plant to plant spacing (inch)",
      "depth (inch)",
      "soil type",
    ];
    for (const field of dropdownFields) {
      if (!formData[field]) {
        newErrors[field] = `Please select ${field}.`;
        isValid = false;
      }
    }

    setErrors(newErrors);
    return isValid;
  };
  const handleSubmission = async () => {
    const isValid = validateForm();
    if (!isValid) {
      console.log("Validation Errors:", errors);
      showAlert("error", "Please fill in all required fields.");
      return;
    }
    try {
      const formatDate = (date) => {
        const pad = (num) => (num < 10 ? `0${num}` : `${num}`);
        return date
          ? `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
              date.getDate()
            )}`
          : null;
      };
      const selectDate = formatDate(selectedDate);
      const postData = {
        planting_date: selectDate || "",
        bed_size: formData["bed size (inch)"],
        row_to_row_spacing: formData["row to row spacing (inch)"],
        plant_to_plant_spacing: formData["plant to plant spacing (inch)"],
        depth: formData["depth (inch)"],
        soil_type: formData["soil type"],

        comments: comment,
      };
      if (isEditMode) {
        postData.id = existingData.id;
      } else {
        postData.farmer_plot = farmerPlotId;
      }
      const apiUrl = isEditMode
        ? "plotengagement/update-planting"
        : "plotengagement/create-planting";
      instance
        .post(apiUrl, postData)
        .then((res) => {
          if (res?.data) {
            setShowSuccessPopup(true);
            setTimeout(() => {
              setShowSuccessPopup(false);
              onClose();
            }, 2000);
          }
        })
        .catch((err) => {
          console.error("Error creating/updating planting", err);
          console.error("Error creating/updating planting");
          showAlert("error", "Some Error Occured");
        });
    } catch (error) {
      console.error("Error", error);
      showAlert("error", "Some Error Occured");
    }
  };
  const handleClose = () => {
    setOpen(false);
    onClose();
  };

  return (
    <div>
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="xs"
        style={{ zIndex: 1 }}
        PaperProps={{ style: { maxHeight: "calc(100% - 25%)" } }}
      >
        <div style={{ padding: "16px" }}>
          <Grid container>
            <Grid item xs={10}>
              <DialogTitle style={{ padding: "0px" }}>
                {t("Planting")}
              </DialogTitle>
            </Grid>
            {existingData !== null ? (
              <Grid item xs={1} style={{ marginTop: "1px" }}>
                <img src={EditIcon} onClick={handleEditClick} />
              </Grid>
            ) : null}
            <Grid item xs={1}>
              <IconButton
                aria-label="close"
                className={classes.closeButton}
                onClick={handleClose}
              >
                <CloseIcon />
              </IconButton>
            </Grid>
          </Grid>
          <Grid container>
            <Grid item xs={7} style={{ marginTop: "20px" }}>
              <InputLabel style={{ fontSize: "12px" }}>
                {t("Date of Planting")}
              </InputLabel>
            </Grid>

            <Grid item xs={5}>
              <TextField
                placeholder="DD/MM/YYYY"
                fullWidth
                disabled={!isEditMode && existingData !== null}
                value={selectedDate ? selectedDate.toLocaleDateString() : ""}
                error={dateError}
                helperText={errors.date || ""}
                InputProps={{
                  sx: {
                    height: "40px",
                    borderRadius: "5px", // You can adjust this if needed
                    marginTop: "10px",
                    background: "#FFF",
                    border: "1px solid #C7BDBD",
                  },
                  readOnly: true,
                  onClick: () => document.getElementById("date-picker").click(),
                }}
              />
            </Grid>
            <DatePicker
              id="date-picker"
              dateFormat="dd/MM/yyyy"
              selected={selectedDate}
              onChange={handleDateChange}
              className={classes.datepicker}
            />
          </Grid>
          <div style={{ ...scrollableContainerStyle }}>
            {rows.map((row, index) => (
              <Grid key={index} container style={{ marginTop: "10px" }}>
                <Grid item xs={typeof row.options[0] === "number" ? 9 : 6}>
                  <InputLabel className={classes.taxtLabel}>
                    {t(row.label)}
                  </InputLabel>
                </Grid>
                <Grid item xs={typeof row.options[0] === "number" ? 3 : 6}>
                  <Select
                    value={formData[row.key.toLowerCase()] || ""}
                    disabled={!isEditMode && existingData !== null}
                    onChange={(event) =>
                      handleChange(row.key.toLowerCase(), event.target.value)
                    }
                    style={{
                      width:
                        typeof row.options[0] === "number"
                          ? "60px"
                          : typeof row.options[0] === "string"
                          ? "140px"
                          : "100%",
                    }}
                    sx={{
                      height: "40px",
                      border: "1px solid #9C9FA0",
                      borderRadius: "5px",
                    }}
                    error={!!errors[row.key.toLowerCase()]}
                    helperText={errors[row.key.toLowerCase()] || ""}
                  >
                    {row.options.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>

                  {errors[row.key.toLowerCase()] && (
                    <InputLabel
                      style={{
                        fontSize: "8px",
                        color: "red",
                        marginTop: "5px",
                        whiteSpace: "normal",
                      }}
                    >
                      {errors[row.key.toLowerCase()]}
                    </InputLabel>
                  )}
                </Grid>
              </Grid>
            ))}
          </div>
        </div>
        <div style={{ display: "grid", padding: "10px" }}>
          <InputLabel>{t("Comment(Optional)")}</InputLabel>
          <TextField
            multiline
            rows={1}
            value={comment}
            onChange={handleCommentChange}
            disabled={!isEditMode && existingData !== null}
          />
        </div>

        <div
          style={{ display: "flex", margin: "auto", justifyContent: "center" }}
        >
          {existingData && isEditMode ? (
            <div style={{ width: "140px" }}>
              <CustomButton onClick={handleSubmission} hideIcon>
                {t("Update")}
              </CustomButton>
            </div>
          ) : !existingData && !isEditMode ? (
            <div
              style={{
                display: "flex",
                margin: "auto",
                justifyContent: "center",
                width: "140px",
              }}
            >
              <CustomButton onClick={handleSubmission} hideIcon>
                {t("Submit")}
              </CustomButton>
            </div>
          ) : null}
        </div>
        {showSuccessPopup && (
          <SuccessPopup onClose={() => setShowSuccessPopup(false)} />
        )}
      </Dialog>
    </div>
  );
};

export default PlantingPopup;
